<template>
  <page-container
    title="Payment Information"
    :disable-footer-button="paymentInProgress"
    :narrow="false"
    @primary-cta-click="onPaymentSubmit"
    @back-cta-click="goToBackPage"
  >
    <template #content>
      <pp-container
        class="pa-6"
      >
        <p
          v-if="selectedAppointmentTypeFeeInDollars"
          data-cy-id="payment-message-step-payment"
          class="mb-3 pa-3 payment-info"
        >
          {{ appointmentWithFeeMessage }}
        </p>

        <pp-loader v-if="isLoading" />

        <pp-payment-form
          v-else-if="invoiceId && merchant"
          ref="paymentForm"
          form-header="Cardholder Information"
          class="payment-form justify-center"
          data-cy-id="payment-form"
          :show-submit-button="false"
          :invoice-amount="invoiceAmount"
          :invoice-id="invoiceId"
          :payrix-script="payrixScript"
          :payrix-merchant-id="merchant.externalId"
          :payrix-api-key="merchant.publicKey"
          @form-loaded="onFormLoad"
          @form-failed="onFormError"
          @payment-failed="onPaymentError"
          @payment-validation-failed="onPaymentValidationError"
          @payment-succeeded="onPaymentSuccess"
        />

        <pp-form-message
          v-else
          type="error"
          data-cy-id="payment-form-error"
          text="Couldn't load a payment form"
        />
      </pp-container>
    </template>
  </page-container>
</template>
<script>
import Vuex, { mapActions, mapState } from 'vuex'
import PageContainer from '@/components/page-container/PageContainer'
import { APPT_FEE_PARAGRAPH, REFERRER_BACK_BUTTON } from '@/views/constants'
import { datadogRum } from '@datadog/browser-rum'

const { mapGetters } = Vuex

export default {
  name: 'ViewsPayment',
  components: {
    'page-container': PageContainer
  },
  data () {
    return {
      isLoading: true,
      paymentInProgress: false,
      merchant: null,
      invoiceId: null,
      apptFeeParagraph: APPT_FEE_PARAGRAPH,
      payrixScript: window.Cypress ? window.Cypress.env('VUE_APP_PAYMENTS_PAYRIX_PAY_FIELDS_SCRIPT') : process.env.VUE_APP_PAYMENTS_PAYRIX_PAY_FIELDS_SCRIPT
    }
  },

  computed: {
    ...mapGetters({
      formData: 'formData/formData',
      selectedAppointmentType: 'formData/selectedAppointmentType',
      selectedAppointmentTypeFeeInDollars: 'formData/selectedAppointmentTypeFeeInDollars',
      selectedAppointmentTypeFeeInCents: 'formData/selectedAppointmentTypeFeeInCents',
      filledSteps: 'ui/filledSteps'
    }),
    ...mapState('legacy', ['createdAppointment']),
    appointmentWithFeeMessage () {
      return `${this.apptFeeParagraph} $${this.selectedAppointmentTypeFeeInDollars}.`
    }
  },
  created () {
    if (!this.filledSteps.stepC) {
      datadogRum.addAction('Payment step opened by directUrl', {
        message: 'Payment step opened, but prev step(s) was not filled',
        formData: {
          newPatient: this.formData.newPatient,
          isTelehealth: this.formData.isTelehealth,
          reason: this.formData.reason,
          insuranceProviderId: this.formData.insuranceProviderId,
          doctorId: this.formData.doctorId,
          locationId: this.formData.locationId,
          timeBlock: this.formData.timeBlock,
          calendarIndex: this.formData.calendarIndex,
          calendarId: this.formData.calendarId,
          scheduleId: this.formData.scheduleId,
          calendarBlockId: this.formData.calendarBlockId
        },
        practice: {
          id: this.$practiceId,
          step: 'Payment'
        },
        redirectedTo: 'stepA'
      })

      this.$router.replace({ name: 'stepA', query: this.$route.query || {} })
    }
  },
  async mounted () {
    this.invoiceAmount = this.selectedAppointmentTypeFeeInCents

    const description = this.selectedAppointmentType.name
    const {
      firstName,
      lastName,
      dateOfBirth,
      email,
      phone: mobilePhone
    } = this.formData

    try {
      this.merchant = await this.getMerchantByPractice()
      this.invoiceId = await this.createSyntheticInvoice({
        firstName,
        lastName,
        dateOfBirth,
        email,
        mobilePhone,
        description,
        source: 'appointments',
        amount: this.selectedAppointmentTypeFeeInDollars,
        sourceId: this.createdAppointment.appointmentId
      })
      this.trackEvent({ action: 'payment_step_load' })
    } catch (e) {
      this.trackEvent({ action: 'payment_step_error' })
    } finally {
      this.isLoading = false
    }
  },
  methods: {
    ...mapActions({
      getMerchantByPractice: 'payment/getMerchantByPractice',
      createSyntheticInvoice: 'payment/createSyntheticInvoice'
    }),
    onFormLoad () {
      this.trackEvent({ action: 'payment_form_load' })
    },
    onFormError () {
      this.trackEvent({ action: 'payment_form_error' })
    },
    onPaymentSubmit () {
      this.paymentInProgress = true
      this.$refs.paymentForm.submitPayment()
      this.trackEvent({ action: 'payment_submit_form_submit' })
    },
    onPaymentSuccess () {
      this.trackEvent({ action: 'payment_submit_success' })

      if (this.createdAppointment.registrationLink && this.createdAppointment.redirectToRegistration) {
        window.parent.location.replace(this.createdAppointment.registrationLink)
      } else {
        this.$router.replace({ name: 'confirmation', query: this.$route.query || {} })
      }
    },
    onPaymentError () {
      this.paymentInProgress = false
      this.trackEvent({ action: 'payment_submit_error' })
    },
    onPaymentValidationError () {
      this.paymentInProgress = false
      this.trackEvent({ action: 'payment_submit_validation_error' })
    },
    goToBackPage () {
      this.$router.replace({ name: 'stepC', params: { referrer: REFERRER_BACK_BUTTON }, query: this.$route.query || {} })
    }
  }
}
</script>

<style lang="scss" scoped>

.payment-form,
.payment-info {
  margin: 0 auto;
  max-width: 550px;
}

</style>
